import PageHead from './PageHead';
import { getSeo, getSeoTitle, getSeoDescription, getSeoKeywords } from '@/seo';
import { useLanguageContext } from '@/i18n/LanguageContext';

/**
 * 各ページに 1 つ置くだけで SEO ヘッドを出力するラッパー。
 * route キーで src/seo/seo-metadata.json から title/description/keywords/canonical/noindex を引き、
 * PageHead に渡す（OGP/Twitter/canonical/noindex/JSON-LD/hreflang 対応）。
 * lang が 'en' のとき title_en/description_en/keywords_en を優先（なければ ja フォールバック）。
 *
 * 使い方:  <Seo route="/courses/essential" />
 * 動的ページで実パスを使いたい場合は path を上書き:  <Seo route="/news/:slug" path={`/news/${slug}`} />
 * FAQ を持つページは faqItems を渡すと FAQPage JSON-LD + speakable を自動注入:
 *   <Seo route="/faq" faqItems={[{ question: 'Q', answer: 'A' }]} />
 */
interface FaqItem {
  question: string;
  answer: string;
}

interface SeoProps {
  route: string;
  /** canonical/OGP url を実パスで上書き（:slug 等の動的ページ用） */
  path?: string;
  /** ページ固有 JSON-LD を追加 */
  jsonLd?: object | object[];
  /** FAQPage JSON-LD + speakable を注入する FAQ リスト */
  faqItems?: FaqItem[];
}

export default function Seo({ route, path, jsonLd, faqItems }: SeoProps) {
  const { lang } = useLanguageContext();
  const s = getSeo(route);
  if (!s) return null;
  const title = getSeoTitle(s, lang);
  const description = getSeoDescription(s, lang);
  const keywords = getSeoKeywords(s, lang);

  // ブログ記事ページでは citation_author メタを付与
  const isBlogPage = route === '/blog' || route.startsWith('/blog/');

  // FAQPage JSON-LD（speakable 含む）を生成して jsonLd に追記
  const jsonLdItems: object[] = [];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) jsonLdItems.push(...jsonLd);
    else jsonLdItems.push(jsonLd);
  }
  if (faqItems && faqItems.length > 0) {
    jsonLdItems.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.faq-question', '.faq-answer'],
      },
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return (
    <PageHead
      title={title}
      rawTitle
      ogImage={s.ogImage}
      ogType={s.ogType}
      description={description}
      path={path ?? s.route}
      keywords={keywords.join(', ')}
      noindex={s.noindex}
      jsonLd={jsonLdItems.length > 0 ? jsonLdItems : undefined}
      lang={lang}
      citationAuthor={isBlogPage ? '日本執事アカデミー講師' : undefined}
    />
  );
}
